import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Plus, Edit, Trash2, Copy, RefreshCw, ExternalLink, Users } from 'lucide-react';
import { toast } from 'sonner';
import { FormField } from './FormField';

const EMPTY = { name: '', company: '', email: '', phone: '', notes: '' };

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/suppliers');
      setSuppliers(res.data);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setPanelOpen(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({ ...EMPTY, ...s }); setPanelOpen(true); };

  const save = async () => {
    if (!form.name || !form.email) { toast.error('Nome ed email obbligatori'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/suppliers/${editing}`, form);
        toast.success('Fornitore aggiornato');
      } else {
        await api.post('/suppliers', form);
        toast.success('Fornitore creato');
      }
      setPanelOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm('Eliminare questo fornitore?')) return;
    await api.delete(`/suppliers/${id}`);
    setSuppliers(prev => prev.filter(s => s.id !== id));
    toast.success('Fornitore eliminato');
  };

  const regenerateToken = async (id) => {
    if (!confirm('Rigenera il link di accesso? Il vecchio link non funzionerà più.')) return;
    try {
      const res = await api.post(`/suppliers/${id}/regenerate-token`);
      setSuppliers(prev => prev.map(s => s.id === id ? { ...s, access_token: res.data.access_token } : s));
      toast.success('Link rigenerato');
    } catch { toast.error('Errore rigenerazione'); }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/fornitore/${token}`;
    navigator.clipboard.writeText(link).then(() => toast.success('Link copiato!'));
  };

  const portalUrl = (token) => `${window.location.origin}/fornitore/${token}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Gestione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Fornitori</h2>
        </div>
        <button onClick={openCreate} className="btn-gold flex items-center gap-2" data-testid="new-supplier-btn">
          <Plus size={14} /> Nuovo Fornitore
        </button>
      </div>

      <div className="card-dark rounded-lg p-4 border border-[#c9a84c]/20 bg-[#c9a84c]/5">
        <p className="text-[#c9a84c] text-xs font-medium mb-1">Come funziona il portale fornitore</p>
        <p className="text-[var(--lux-text-muted)] text-xs leading-relaxed">
          Ogni fornitore riceve un <strong className="text-white">link univoco</strong> (senza password) per accedere al suo portale personale.
          Da lì può vedere tutte le richieste di servizi che lo riguardano e inserire direttamente il proprio preventivo.
          L'admin vedrà il costo inserito nella scheda richiesta e potrà applicare la maggiorazione configurata prima di inviare il preventivo all'ospite.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div>
      ) : suppliers.length === 0 ? (
        <div className="card-dark rounded-lg p-16 text-center">
          <Users size={36} className="text-[var(--lux-border-3)] mx-auto mb-4" strokeWidth={1} />
          <p className="text-[var(--lux-text-muted-3)] text-sm">Nessun fornitore. Aggiungine uno per iniziare.</p>
        </div>
      ) : (
        <div className="card-dark rounded-lg overflow-hidden">
          <table className="w-full table-dark">
            <thead><tr>
              <th className="text-left">Fornitore</th>
              <th className="text-left hidden md:table-cell">Email</th>
              <th className="text-left hidden lg:table-cell">Telefono</th>
              <th className="text-left">Link Portale</th>
              <th />
            </tr></thead>
            <tbody>
              {suppliers.map(s => (
                <tr key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <td>
                    <p className="text-white font-medium">{s.name}</p>
                    {s.company && <p className="text-[var(--lux-text-muted-3)] text-xs">{s.company}</p>}
                  </td>
                  <td className="hidden md:table-cell text-[var(--lux-text-muted)] text-sm">{s.email}</td>
                  <td className="hidden lg:table-cell text-[var(--lux-text-muted)] text-sm">{s.phone || '—'}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <code className="text-[#c9a84c] text-[10px] bg-[var(--lux-surface-3)] px-2 py-1 rounded font-mono">
                        …/{s.access_token.slice(-8)}
                      </code>
                      <button onClick={() => copyLink(s.access_token)} title="Copia link" className="p-1 text-[var(--lux-text-muted-3)] hover:text-[#c9a84c]">
                        <Copy size={13} />
                      </button>
                      <a href={portalUrl(s.access_token)} target="_blank" rel="noopener noreferrer"
                        className="p-1 text-[var(--lux-text-muted-3)] hover:text-[#c9a84c]" title="Apri portale">
                        <ExternalLink size={13} />
                      </a>
                    </div>
                  </td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => openEdit(s)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-white"><Edit size={13} /></button>
                      <button onClick={() => regenerateToken(s.id)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-amber-400" title="Rigenera link">
                        <RefreshCw size={13} />
                      </button>
                      <button onClick={() => remove(s.id)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-red-400"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="bg-[var(--lux-surface-1)] border-l border-[#222] text-white w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl font-light text-white">
              {editing ? 'Modifica Fornitore' : 'Nuovo Fornitore'}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <FormField label="Nome *" name="name" form={form} setForm={setForm} testId="supplier-name" />
            <FormField label="Azienda / Ragione Sociale" name="company" form={form} setForm={setForm} testId="supplier-company" />
            <FormField label="Email *" name="email" type="email" form={form} setForm={setForm} testId="supplier-email" />
            <FormField label="Telefono" name="phone" form={form} setForm={setForm} testId="supplier-phone" />
            <FormField label="Note interne" name="notes" type="textarea" rows={3} form={form} setForm={setForm} testId="supplier-notes" />
            {editing && (
              <div className="bg-[var(--lux-surface-3)] rounded p-3">
                <p className="text-[var(--lux-text-muted-2)] text-xs mb-1">Link portale fornitore:</p>
                <p className="text-[#c9a84c] text-xs font-mono break-all">{portalUrl(suppliers.find(s=>s.id===editing)?.access_token||'')}</p>
              </div>
            )}
            <div className="flex gap-3 pt-4 border-t border-[#222]">
              <button onClick={save} disabled={saving} className="btn-gold flex-1">
                {saving ? <Loader2 size={14} className="animate-spin mx-auto" /> : 'Salva'}
              </button>
              <button onClick={() => setPanelOpen(false)} className="btn-outline-gold flex-1">Annulla</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
